import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {formValueSelector, change, Field} from 'redux-form'
import {FormattedMessage} from 'react-intl';
import {connect} from 'react-redux';
import {
    DOCUMENTATION_UPDATE_SECTION,
    FORM_FIELD_DOC_UPDATE_NEEDED,
    FORM_FIELD_ESTIMATED_DATE_OF_MODIFICATION,
    FORM_FIELD_ROOT_CAUSE, FORM_FIELD_SOLUTION_GROUP,
    FORM_FIELD_TYPE_OF_REQUEST,
    FORM_FIELD_UPDATE_FILE_NUMBER,
    FORM_FIELD_FINAL_PART_REFERENCE,
    FORM_LABEL_DOC_UPDATE_NEEDED,
    FORM_LABEL_ESTIMATED_DATE_OF_MODIFICATION,
    FORM_LABEL_ROOT_CAUSE,
    FORM_LABEL_UPDATE_FILE_NUMBER,
    FORM_LABEL_FINAL_PART_REFERENCE,
    FORM_TICKET_SECTION,
    FORM_BUTTON_ANSWER,
} from '../../../../../../constants/formConstants';
import {InputFieldWithValidation} from '../../../../../common/InputFieldWithValidation';
import formMessages from '../../../../../../intl/technicalDocumentations/formMessages';
import DatePickerComponent from '../../../../../common/datePicker/DatePickerComponent';
import moment from 'moment';
import commonMessages from '../../../../../../intl/common/commonMessages';
import {
    validPartNoLength
} from '../../../../../common/validationCommons';
import InputSelectFieldWithValidation from '../../../../../common/InputSelectFieldWithValidation';
import {
    PART_DOC_REQUEST,
    yesProblemCategoryList,
    noProblemCategoryList,
    problemCategoryList,
    problemCategoryExceptions
} from '../../../../../../constants/Utils';
import problemCategoryMessages from '../../../../../../intl/technicalDocumentations/problemCategoryMessages';
import {moduleRoles} from "../../../../../../utils/roles";
import get from "get-value";
import {first, isEmpty} from "lodash";
import {required} from '../../../../../common/validationCommons';
import PropTypes from 'prop-types';

const StyledDiv = styled.div`
  border-radius: 5px;
`;

const DocumentationUpdateSectionTD = (props) => {
    const { readOnly, reqFields, userRights, updateFileNumber, updateFileNumberNeeded, change, selectedRequestType, userRoles, solutionGroupId, initialValues, roles, assignedSolutionGroup, submitBy, isTransferingToL3 } = props;
    const isDisabled = selectedRequestType === PART_DOC_REQUEST ?
        !userRights.canEditPartDocDocumentationUpdate || readOnly || !moduleRoles.isSGEditorLVL23Documentation(userRoles) :
        !userRights.canEditDocumentationUpdate || readOnly;

    const selectedSolutionGroup = useMemo(() => {
        const sgGroups = (!userRights.canEditInternalTicketRequestDetail
            || readOnly) ?
            [get(initialValues, 'requestorSolutionGroup')] : // read => get current solution group
            get(roles, 'TECHNICAL_DOCUMENTATION.sgEditor.sgPermission'); // edit => offer users solution groups
        return !isEmpty(sgGroups) ? first(sgGroups.filter(group => get(group, 'id', '') === solutionGroupId)) : {};
    }, [userRights.canEditInternalTicketRequestDetail, readOnly, initialValues, roles, solutionGroupId]);

    const rootCauseOptions = useMemo(() => {
        // Get the base list based on updateFileNumberNeeded
        const baseList = !updateFileNumberNeeded ? problemCategoryList : 
                        updateFileNumberNeeded === 'YES' ? yesProblemCategoryList : 
                        noProblemCategoryList;
        
        // Filter out exceptions for specific request types
        const exceptions = problemCategoryExceptions[selectedRequestType] || [];
        return exceptions.length > 0 ? baseList.filter(item => !exceptions.includes(item)) : baseList;
    }, [updateFileNumberNeeded, selectedRequestType]);

    const isL3Required = !readOnly &&
    !isTransferingToL3 && (
            (selectedSolutionGroup ? get(selectedSolutionGroup,
                'name', '').toLowerCase().includes('l3') && (updateFileNumberNeeded
                    === 'YES' || updateFileNumberNeeded === 'NO') : false)
                    ||
                    (assignedSolutionGroup ? get(assignedSolutionGroup, 'name',
                        '').toLowerCase().includes('l3') && (updateFileNumberNeeded
                === 'YES' || updateFileNumberNeeded === 'NO') : false)
            );

    const isDateModificationRequired = !readOnly &&
        !isTransferingToL3 && (
            (selectedSolutionGroup ? get(selectedSolutionGroup,
                'name', '').toLowerCase().includes('l3') && (updateFileNumberNeeded
                === 'YES') : false)
            ||
            (assignedSolutionGroup ? get(assignedSolutionGroup, 'name',
                '').toLowerCase().includes('l3') && (updateFileNumberNeeded
                === 'YES') : false)
        );

    const handleChangeDocUpdate = (event, newValue) => {
        event.preventDefault();
        change(event.target.name, newValue);
        change(`${DOCUMENTATION_UPDATE_SECTION}.${FORM_FIELD_ROOT_CAUSE}`, "");
    };

    return (
        <>
            <h3 className="text-left mt-4 ml-4">
                <FormattedMessage {...formMessages.DOCUMENTATION_UPDATE_SECTION}/>
            </h3>
            <StyledDiv className="border border-secondary px-4 py-2 bg-white">
                <div className="form-row">
                    <div className="col-xl-2 col-md-4">
                        <InputSelectFieldWithValidation label={formMessages[FORM_LABEL_DOC_UPDATE_NEEDED]}
                                                        field={FORM_FIELD_DOC_UPDATE_NEEDED}
                                                        options={['YES', 'NO']}
                                                        intlMessages={commonMessages}
                                                        isRequired={(!get(selectedSolutionGroup, 'isDefault', false) && !isDisabled) || reqFields.includes(FORM_FIELD_DOC_UPDATE_NEEDED)}
                                                        isDisabled={isDisabled}
                                                        handleChange={handleChangeDocUpdate}
                        />
                    </div>
                    <div className="col-xl-2 col-md-4">
                        <InputFieldWithValidation label={formMessages[FORM_LABEL_UPDATE_FILE_NUMBER]}
                                                  field={FORM_FIELD_UPDATE_FILE_NUMBER}
                                                  maxLength={25}
                                                  isDisabled={isDisabled || (updateFileNumberNeeded === 'NO')}
                                                  isRequired={reqFields.includes(FORM_FIELD_UPDATE_FILE_NUMBER)}
                                                  isSmall
                        />
                    </div>
                    <div className="col-xl-2 col-md-4">
                        <Field component={DatePickerComponent}
                               selectsStart
                               label={formMessages[FORM_LABEL_ESTIMATED_DATE_OF_MODIFICATION]}
                               name={FORM_FIELD_ESTIMATED_DATE_OF_MODIFICATION}
                               parse={(e) => e ? moment(e).format('YYYY-MM-DD') : null}
                               validate={isDateModificationRequired ? [required] : []}
                               isRequired={isDateModificationRequired}
                               isDisabled={get(selectedSolutionGroup, 'name', '').toLowerCase().includes('l2') || get(assignedSolutionGroup, 'name', '').toLowerCase().includes('l2') || isDisabled}
                        />
                    </div>
                    <div className="col-xl-2 col-md-4">
                        <InputSelectFieldWithValidation label={formMessages[FORM_LABEL_ROOT_CAUSE]}
                                                        field={FORM_FIELD_ROOT_CAUSE}
                                                        options={rootCauseOptions}
                                                        intlMessages={problemCategoryMessages}
                                                        isRequired={isL3Required}
                                                        isDisabled={isDisabled}
                        />
                    </div>
                    <div className="col-xl-2 col-md-4">
                        <InputFieldWithValidation label={formMessages[FORM_LABEL_FINAL_PART_REFERENCE]}
                                                  field={FORM_FIELD_FINAL_PART_REFERENCE}
                                                  maxLength={10}
                                                  validations={[validPartNoLength]}
                                                  pattern="[A-Za-z0-9]+"
                                                  isDisabled={isDisabled}
                                                  isRequired={isL3Required}
                                                  isSmall
                        />
                    </div>
                </div>
            </StyledDiv>
        </>
    )
}

DocumentationUpdateSectionTD.propTypes = {
    readOnly: PropTypes.bool,
    reqFields: PropTypes.array.isRequired,
    userRights: PropTypes.object.isRequired,
    updateFileNumber: PropTypes.string,
    updateFileNumberNeeded: PropTypes.string,
    change: PropTypes.func.isRequired,
    selectedRequestType: PropTypes.string,
    userRoles: PropTypes.array,
    solutionGroupId: PropTypes.string,
    initialValues: PropTypes.object,
    roles: PropTypes.object,
    assignedSolutionGroup: PropTypes.object,
    submitBy: PropTypes.string,
    isTransferingToL3: PropTypes.bool,
};

const selector = (form, ...other) => (formValueSelector(form))(...other);
const mapStateToProps = (state, initialProps) => ({
    userRoles: state.profile.userDetail.roles,
    updateFileNumber: selector(initialProps.form, state, `${DOCUMENTATION_UPDATE_SECTION}.${FORM_FIELD_UPDATE_FILE_NUMBER}`),
    updateFileNumberNeeded: selector(initialProps.form, state, `${DOCUMENTATION_UPDATE_SECTION}.${FORM_FIELD_DOC_UPDATE_NEEDED}`),
    selectedRequestType: selector(initialProps.form, state, `${FORM_TICKET_SECTION}.${FORM_FIELD_TYPE_OF_REQUEST}`),
    solutionGroupId: selector(initialProps.form, state, `${FORM_TICKET_SECTION}.${FORM_FIELD_SOLUTION_GROUP}`),
    isTransferingToL3: selector(initialProps.form, state, 'isTransferingToL3'),
    initialValues: initialProps,
    roles: state.profile.userDetail.roles,
    submitBy: selector(initialProps.form, state, 'submitBy'),
});

const mapDispatchToProps = (dispatch, props) => ({
    change: (name, value) => dispatch(change(props.form, name, value)),
});


export default connect(mapStateToProps, mapDispatchToProps)(DocumentationUpdateSectionTD);
